/**
 * What providers said that disagrees with a resident's building (2026-10-28, docs/provider/CE_PULL.md).
 *
 * Above the No building list: a provider at a building tried to add a resident from CounselEAR, and either was refused
 * (the person's CounselEAR Preferred Clinic is confirmed as a building the provider does not cover: nobody was added)
 * or the resident lives at another building than the provider's. The provider says the person is at THEIR building;
 * the office decides whether the Preferred Clinic in CounselEAR (and so the building) is out of date. Read-only.
 * Renders nothing when there is nothing to say, before the deploy, or for a facility-scoped reviewer.
 */
import React, { useEffect, useState } from 'react';
import { noBuildingApi, type ProviderStatement } from './noBuildingApi';

const ZONE = 'America/New_York';
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ZONE }) : '—');

function sentence(s: ProviderStatement): string {
  const who = s.by || 'A provider';
  const at = s.provider_building.name || 'their building';
  if (s.outcome === 'CLINIC_ELSEWHERE') {
    const clinic = s.clinic ? (s.clinic.name || `clinic ${s.clinic.id}`) : 'their clinic';
    return `${who} tried to add CounselEAR ${s.counselear_id || '(no id)'} at ${at} on ${when(s.at)} and was refused: their CounselEAR Preferred Clinic (${clinic}) is confirmed as ${s.clinic_building?.name || 'another building'}. The provider says they are at ${at}. Nobody was added.`;
  }
  const name = s.resident ? [s.resident.first_name, s.resident.last_name].filter(Boolean).join(' ') || 'Name not recorded' : 'A resident';
  return `${who} ${s.outcome === 'CREATED' ? 'added' : 'looked up and linked'} ${name} (CounselEAR ${s.counselear_id || '—'}) at ${at} on ${when(s.at)}; eMerge has them at ${s.resident?.building?.name || 'another building'}. The provider says they are at ${at}.`;
}

export default function ProviderStatements() {
  const [items, setItems] = useState<ProviderStatement[]>([]);
  useEffect(() => {
    let live = true;
    // Never fails the desk: a failed or missing read shows nothing here.
    try {
      Promise.resolve(noBuildingApi.statements()).then((r) => {
        if (live && r && r.success && r.data && !r.data.enterprise_only) setItems(r.data.items || []);
      }).catch(() => {});
    } catch { /* nothing to show */ }
    return () => { live = false; };
  }, []);
  if (!items.length) return null;
  return (
    <section className="mf-panel" aria-label="What providers said">
      <h2>What providers said (last 30 days)</h2>
      <p>
        A provider adding a resident from CounselEAR said they are at a different building than eMerge has. If the
        provider is right, the resident&apos;s Preferred Clinic in CounselEAR is out of date: fix it there, then confirm the
        clinic&apos;s building here.
      </p>
      <ul>
        {items.map((s) => <li key={s.id}>{sentence(s)}</li>)}
      </ul>
    </section>
  );
}
